import React from "react";
import PropTypes from "prop-types";
import { TextAreaComponent } from "@syncfusion/ej2-react-inputs";
import classNames from "classnames";

/**
 * TextAreaInput — Syncfusion TextAreaComponent wrapper with project-standard styling.
 *
 * Uses the outline variant with floating labels for a modern Material-like
 * appearance. Integrates with react-hook-form via Controller's `value`/`onChange`.
 *
 * @example
 * <TextAreaInput placeholder="Comments" value={field.value} onChange={field.onChange} rows={3} />
 */
export default function TextAreaInput({
  placeholder,
  value,
  onChange,
  error,
  rows = 3,
  maxLength,
  showCharCount = false,
  disabled = false,
  className,
  floatLabelType = "Auto",
  ...props
}) {
  const charLength = (value ?? "").length;

  return (
    <div className={classNames("text-area-input-wrapper w-full", className)}>
      <div className={classNames("rounded-md", { "ring-1 ring-red-500": !!error })}>
        <TextAreaComponent
          placeholder={placeholder}
          floatLabelType={floatLabelType}
          value={value ?? ""}
          input={e => onChange?.(e.value)}
          rows={rows}
          maxLength={maxLength}
          width="100%"
          enabled={!disabled}
          cssClass={classNames("e-outline", { "e-error": !!error })}
          {...props}
        />
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {showCharCount && maxLength > 0 && (
        <p className="mt-1 text-right text-xs text-gray-400">{charLength}/{maxLength}</p>
      )}
    </div>
  );
}

TextAreaInput.propTypes = {
  /** Floating-label / placeholder text */
  placeholder: PropTypes.string,
  /** Controlled value */
  value: PropTypes.string,
  /** Change handler — receives the raw string value */
  onChange: PropTypes.func,
  /** Validation error message */
  error: PropTypes.string,
  /** Number of visible text rows */
  rows: PropTypes.number,
  /** Maximum character length */
  maxLength: PropTypes.number,
  /** Show a live character counter below the textarea (requires maxLength) */
  showCharCount: PropTypes.bool,
  /** Fully disabled (greyed-out, not focusable) */
  disabled: PropTypes.bool,
  /** Additional CSS classes for the wrapper */
  className: PropTypes.string,
  /** Syncfusion float-label type */
  floatLabelType: PropTypes.oneOf(["Auto", "Always", "Never"]),
};
